package com.yendo.mx.models

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.firestore.FieldValue // 🚀 IMPORTANTE: Para la transacción atómica
import dev.gitlive.firebase.firestore.firestore
import dev.gitlive.firebase.firestore.where
import dev.gitlive.firebase.storage.storage
import kotlinx.datetime.Clock
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.catch
import com.yendo.mx.utils.getAhora
import com.yendo.mx.utils.toStorageData
import com.yendo.mx.utils.redondear

object PedidosManager {
    private val db = Firebase.firestore
    private val auth = Firebase.auth

    suspend fun enviarPedido(
        tipoPedido: String,
        idZona: String,
        esProgramado: Boolean,
        fechaProgramada: Long?,
        subtotal: Double = 0.0,
        costoEnvio: Double = 0.0,
        tarifaServicio: Double = 0.0,
        propina: Double = 0.0,
        descuento: Double = 0.0,
        totalPagado: Double = 0.0,
        metodoPago: String,
        conCuantoPaga: Double? = null,
        cambioAEntregar: Double? = null,
        nombreRestaurante: String = "",
        logoRestaurante: String = "",
        direccionNegocio: String = "",
        latNegocio: Double = 0.0,
        lngNegocio: Double = 0.0,
        indicacionesNegocio: String = "",
        direccionEntrega: String,
        latitud: Double = 0.0,
        longitud: Double = 0.0,
        tipoUbicacion: String = "Casa",
        opcionEntrega: String = "Entregar en puerta",
        indicaciones: String = "",
        nombreCliente: String = "",
        apellidoCliente: String = "",
        emailCliente: String = "",
        telefonoCliente: String = "",
        issuerId: String? = null,
        accionAgotado: String? = null,
        pagoPostArmado: Boolean = false,
        idCuponUsado: String? = null,
        idTarjetaSeleccionada: String? = null,
        tokenPagar: String? = null,
        requierePreAutorizacion: Boolean = false
    ): String? {

        val usuarioActual = auth.currentUser
        if (usuarioActual == null) return null

        return try {
            val itemsRequest = if (tipoPedido == "MANDADITO") emptyList() else CartManager.items.map { item ->
                com.yendo.mx.repository.CheckoutItemRequest(
                    idProducto = item.producto.id,
                    cantidad = item.cantidad,
                    adicionales = item.adicionales.map { it.nombre },
                    notas = item.notas
                )
            }

            val request = com.yendo.mx.repository.CheckoutRequest(
                items = itemsRequest,
                idRestaurante = if (tipoPedido == "MANDADITO") "YENDO_LOGISTICS" else (CartManager.restauranteActualId ?: "GENERAL"),
                idZona = idZona,
                ubicacionEntrega = com.yendo.mx.repository.CheckoutUbicacionRequest(
                    latitude = latitud,
                    longitude = longitud,
                    direccion = direccionEntrega,
                    tipoUbicacion = tipoUbicacion,
                    opcionEntrega = opcionEntrega,
                    indicaciones = indicaciones
                ),
                ubicacionNegocio = com.yendo.mx.repository.CheckoutUbicacionRequest(
                    latitude = latNegocio,
                    longitude = lngNegocio,
                    direccion = direccionNegocio,
                    tipoUbicacion = "Mandadito",
                    opcionEntrega = "Mandadito",
                    indicaciones = indicacionesNegocio
                ),
                costoEnvio = costoEnvio,
                tarifaServicio = tarifaServicio,
                metodoPago = metodoPago,
                idTarjetaSeleccionada = idTarjetaSeleccionada,
                tokenPagar = tokenPagar,
                propina = propina,
                idCupon = idCuponUsado,
                tipoPedido = tipoPedido,
                esProgramado = esProgramado,
                fechaProgramada = fechaProgramada,
                conCuantoPaga = conCuantoPaga,
                accionAgotado = accionAgotado ?: "LLAMAR",
                nombreCliente = nombreCliente,
                apellidoCliente = apellidoCliente,
                emailCliente = emailCliente,
                telefonoCliente = telefonoCliente,
                issuerId = issuerId
            )

            val idGenerado = com.yendo.mx.repository.PagosRepository.finalizarPedidoSeguro(request)

            if (idGenerado != null) {
                // 🚀 ANALYTICS: REGISTRAR PURCHASE EN GA4
                try {
                    com.yendo.mx.utils.AdAnalyticsManager.logPurchase(
                        orderId = idGenerado,
                        totalValue = CartManager.totalDinero,
                        itemsCount = CartManager.items.size,
                        merchantName = CartManager.restauranteActualNombre ?: ""
                    )
                } catch (e: Exception) {}

                if (tipoPedido != "MANDADITO") CartManager.vaciarCarrito()
            }
            idGenerado

        } catch (e: Exception) {
            println("YENDO_DEBUG: ERROR FATAL AL ENVIAR CHECKOUT SEGURO: ${e.message}")
            null
        }
    }

    suspend fun guardarUrlPago(idPedido: String, urlMercadoPago: String) {
        try {
            db.collection("Pedidos").document(idPedido).update(
                "url_pago" to urlMercadoPago
            )
        } catch (e: Exception) {
            println("YENDO_DEBUG: Error guardando la URL de pago: ${e.message}")
        }
    }

    fun observarPedidoRealTime(idPedido: String): Flow<PedidoFirebase?> {
        return db.collection("Pedidos")
            .document(idPedido)
            .snapshots
            .map { snapshot ->
                if (snapshot.exists) snapshot.data<PedidoFirebase>() else null
            }
            .catch { emit(null) }
    }

    fun observarTodosLosPedidosActivos(): Flow<List<PedidoFirebase>> {
        val uid = auth.currentUser?.uid ?: return flowOf(emptyList())

        return db.collection("Pedidos")
            .where { "idUsuario" equalTo uid }
            .snapshots
            .map { querySnapshot ->
                querySnapshot.documents
                    .map { it.data<PedidoFirebase>() }
                    .filter { it.estadoActual != "CONCLUIDO" && it.estadoActual != "CANCELADO" }
            }
            .catch { emit(emptyList()) }
    }

    suspend fun subirComprobantePago(idPedido: String, imageBytes: ByteArray): String? {
        return try {
            val storageRef = Firebase.storage.reference("comprobantes_pago/$idPedido.jpg")
            storageRef.putData(imageBytes.toStorageData())
            storageRef.getDownloadUrl()
        } catch (e: Exception) {
            println("YENDO_ERROR: Error subiendo comprobante: ${e.message}")
            null
        }
    }

    suspend fun subirFotoPaquete(idPedido: String, imageBytes: ByteArray): String? {
        return try {
            val storageRef = Firebase.storage.reference("fotos_paquetes/$idPedido.jpg")
            storageRef.putData(imageBytes.toStorageData())
            storageRef.getDownloadUrl()
        } catch (e: Exception) {
            println("YENDO_ERROR: Error subiendo foto paquete: ${e.message}")
            null
        }
    }
}